import React, { useEffect, useRef } from 'react';
import { Tag, Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { getTagForServiceState } from '@src/components/Common/Tags';
import ServiceTabs from './ServiceTabs';
import CollapsePanel from '@src/components/Common/CollapsePanel';
import { ServiceCatalogueConfig } from './types';

/** the tags a service carries, capped so one heavily-tagged service cannot own the row */
const TAG_LIMIT = 4;

/**
 * How much of the window an opened card takes. The panel is that tall whatever
 * is in it — a long schema scrolls, a short readme leaves space — so every
 * service opens onto the same shape, and a bigger display opens onto more of
 * the service rather than more card around it.
 *
 * A share of the window and not of the card it sits in: the card grows with the
 * panel, so sizing the panel off it would feed back on itself.
 */
const PANEL_VIEWPORT_SHARE = 0.6;

/**
 * One service in the catalogue: a card that says what the service is, and
 * opens in place onto everything about it. The card is the control — there is
 * no separate "view" affordance, because opening it is the only thing it does.
 */
const ServiceCard = ({
    record,
    config,
    open,
    toggle,
    scrollIntoView,
    dependant,
}: {
    record: any;
    config: ServiceCatalogueConfig;
    open: boolean;
    toggle: () => void;
    /** a deep link asked for this card; bring it into view once it is rendered */
    scrollIntoView?: boolean;
    dependant?: boolean;
}) => {
    const ref = useRef<HTMLElement>(null);

    useEffect(() => {
        if (scrollIntoView && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [scrollIntoView]);

    const description = record.schema?.schema?.description;
    const tags: string[] = record.tags || [];

    return (
        <article
            ref={ref}
            className={`svc-card${open ? ' svc-card--open' : ''}`}
            // the panel's height, inherited down to it — see PANEL_VIEWPORT_SHARE
            style={{ '--svc-panel-h': `${PANEL_VIEWPORT_SHARE * 100}vh` } as React.CSSProperties}
        >
            <div
                className="svc-card__head"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${record.name}`}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <RightOutlined className="svc-card__chev" aria-hidden="true" />

                <div className="svc-card__body">
                    <div className="svc-card__title">
                        <h3 className="svc-card__name">{record.name}</h3>
                        {!dependant && getTagForServiceState(record.state)}
                        {tags.slice(0, TAG_LIMIT).map((tag) => (
                            <Tag key={tag} className="svc-tag">
                                #{tag}
                            </Tag>
                        ))}
                        {tags.length > TAG_LIMIT && (
                            <Tooltip title={tags.slice(TAG_LIMIT).map((tag) => `#${tag}`).join('  ')}>
                                <Tag className="svc-tag">+{tags.length - TAG_LIMIT}</Tag>
                            </Tooltip>
                        )}
                    </div>

                    {/* what the service is, for choosing between closed cards —
                        the open one is showing the whole of it already */}
                    {description && !open && <p className="svc-card__desc">{description}</p>}
                </div>

                {record.owner?.name && (
                    <span className="svc-card__owner">
                        <span className="svc-card__owner-label">Owner</span>
                        <Tooltip placement="topRight" title={record.owner.name}>
                            <span className="svc-card__owner-name">{record.owner.name}</span>
                        </Tooltip>
                    </span>
                )}
            </div>

            {/* mounted only once open, so a page of cards costs one card's worth
                of requests rather than every tab of every service */}
            <CollapsePanel open={open} className="svc-card__panel">
                <ServiceTabs record={record} config={config} dependant={dependant} />
            </CollapsePanel>
        </article>
    );
};

export default ServiceCard;
